/**
 * Filtered Order service
 */
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  IEntityConfig,
  ENTITY_TYPES,
  IXVIListColumn,
  CELL_FILTER,
  CELL_RENDERER,
  COLUMN_SELECTOR_ID,
} from '@shared/types';

import { OrdersService } from '../orders/orders.service';
import { getSingularEntityName } from '@app/shared/configs/entity-name-config';
import { filterParamsParser, generalTypeParser } from './filtered.util';
import { ValueFormatterParams } from 'ag-grid-community/dist/lib/entities/colDef';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import {
  ORDER_FILTER_STATUS_OPTIONS,
  ORDER_STATUS_ENUM_OPTIONS,
  ORDER_STATUS_VALUE,
} from '@app/shared/types/enums/order-status.enum';
import { I18n } from '@app/i18n/strings-keys';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';

const entityType: ENTITY_TYPES = ENTITY_TYPES.FILTERED_ORDER;
const filteredOrderServiceConfig: IEntityConfig = {
  ENTITY_TYPE: entityType,
  entityNameToCheckPermissions: ENTITY_TYPES.ORDER,
  uiRoute: entityType,
  isMasterListAvailable: true,
  buttons: {},
  editor: {
    entityTitle: getSingularEntityName(entityType),
  },
  apiEndpoint: ENTITY_TYPES.ORDER,
  isFormServiceSupported: false,
  needConvertTimeZoneFields: ['transaction_date', 'delivery_date'],
  list: {
    gridOptions: {
      valueField: '_id',
      multiselect: true,
    },
    isShowModifiedDateByDefault: true,
    isHideModifiedDateFilter: true,
    defaultViewFieldsSetting: {
      requiredSetting: {
        fields: ['uid', 'type', 'date_from', 'date_to'],
      },
      filterModelSetting: {
        status: {
          dependencyField: ['type'],
          fieldsParser: generalTypeParser,
        },
      },
      filterQuerySetting: {
        dependencyField: ['type', 'date_from', 'date_to', 'uid'],
        fieldsParser: filterParamsParser,
      },
      validateMessage: I18n.ErrorMessages.IncorrectUrlMessage,
    },
    documentQuery: {
      orderby: 'transaction_date desc',
      notNeedActive: true,
    },
    columns: [],
    titleTooltipIconConfig$: new BehaviorSubject({
      tooltipConfig: {
        tooltip: I18n.InfoMessages.FilterOrderListTips,
      },
    }),
  },
};

/**
 * Filtered Order service that extends OrdersService to see
 * filtered orders from emails
 */
@Injectable()
export class FilteredOrderService extends OrdersService {
  public static config: IEntityConfig = filteredOrderServiceConfig;
  public static ENTITY_TYPE: string = filteredOrderServiceConfig.ENTITY_TYPE;

  public constructor(public injector: Injector) {
    super(injector);

    this.config.buttons.create = {
      hidden: true,
    };

    const listColumns: IXVIListColumn[] = [
      {
        path: 'internal_number',
        title: I18n.Forms.OrderColumns.PurchaseOrderNo,
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'vendor_name',
        title: I18n.EntityNames.Vendor,
        filterType: CELL_FILTER.string,
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: [ORDER_STATUS_VALUE.Open],
        },
        defaultView: true,
      },
      {
        path: 'transaction_date',
        title: I18n.Forms.OrderColumns.OrderDate,
        filterType: CELL_FILTER.dateLocale,
        sortable: true,
        defaultView: true,
      },
      {
        path: 'delivery_date',
        title: I18n.Common.DeliveryDate,
        filterType: CELL_FILTER.dateLocale,
        sortable: true,
        defaultView: true,
      },
      {
        path: 'total_cost',
        title: I18n.Common.GrandTotal,
        sortable: true,
        defaultView: true,
        valueFormatter: (params: ValueFormatterParams): string => {
          return this.numberFormatService.getNumberFormatter(true)({
            value: params.value,
          });
        },
        ...COLUMN_ALIGN_RIGHT,
      },
      {
        path: 'external_order_number',
        title: I18n.Forms.OrderColumns.ExternalRefNo,
        sortable: true,
        filterParams: {
          quickFilter: true,
        },
        defaultView: true,
      },
      {
        path: 'invoice_number',
        title: I18n.Forms.OrderColumns.InvoiceNo,
        sortable: true,
        filterParams: {
          quickFilter: true,
        },
        defaultView: true,
      },
      {
        path: 'status',
        title: I18n.Common.Status,
        filterType: CELL_FILTER.enum,
        filterParams: {
          values: ORDER_FILTER_STATUS_OPTIONS,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.prepared_string_renderer,
        cellRendererParams: {
          valueToEnumMap: ORDER_STATUS_ENUM_OPTIONS,
        },
        defaultView: true,
        headerComponentParams: {
          enableMenu: false,
        },
      },
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.filtered_order_actions_render,
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ];

    this.config.list.columns = [...listColumns];
  }
}
